import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { fmtInt, fmtPct, pctChange } from './formatters';

/**
 * The six KPI tiles. `kpis` is the API's { current, previous } object.
 * goodWhenUp decides whether a rise is shown in green or red.
 */
export default function KpiCards({ kpis }) {
  const c = kpis?.current ?? {};
  const p = kpis?.previous ?? {};

  const cards = [
    {
      label: 'Activations',
      value: fmtInt(c.activations),
      change: pctChange(c.activations, p.activations),
      goodWhenUp: true,
      sub: 'Successful (ResultCode 0)',
    },
    {
      label: 'New installations',
      value: fmtInt(c.newInstalls),
      change: pctChange(c.newInstalls, p.newInstalls),
      goodWhenUp: true,
      sub: `${fmtInt(c.refreshes)} were refreshes`,
    },
    {
      label: 'Released',
      value: fmtInt(c.released),
      change: pctChange(c.released, p.released),
      goodWhenUp: false,
      sub: 'Deactivated, not replaced',
    },
    {
      label: 'Net installations',
      value: `${c.netInstalls > 0 ? '+' : ''}${fmtInt(c.netInstalls)}`,
      change: pctChange(c.netInstalls, p.netInstalls),
      goodWhenUp: true,
      sub: 'New installations − released',
    },
    {
      label: 'Active installations',
      value: fmtInt(c.activeInstallations),
      change: null, // a current snapshot, so there's no previous-period value
      sub: 'Not deactivated, as of now',
    },
    {
      label: 'Failed attempts',
      value: fmtInt(c.failed),
      note: `${fmtPct(c.failureRate)} of attempts`,
      sub: 'ResultCode other than 0',
    },
  ];

  return (
    <Box
      component="section"
      aria-label="Key metrics"
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 2 }}
    >
      {cards.map((card) => (
        <Paper key={card.label} elevation={1} sx={{ borderRadius: 3, px: 2.5, py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {card.label}
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'baseline', gap: 1.25, my: 0.5 }}>
            <Typography sx={{ fontSize: 32, lineHeight: 1.1 }}>{card.value}</Typography>
            <Change {...card} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {card.sub}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function Change({ change, goodWhenUp, note }) {
  if (note) {
    return <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{note}</Typography>;
  }
  if (change == null || !Number.isFinite(change)) return null;
  const up = change > 0;
  const flat = Math.abs(change) < 0.0005;
  const color = flat ? 'text.secondary' : up === goodWhenUp ? 'success.dark' : 'error.dark';
  return (
    <Typography variant="body2" sx={{ fontWeight: 500, color }} title="Compared with the previous period of the same length">
      {flat ? '—' : `${up ? '▲' : '▼'} ${fmtPct(Math.abs(change))}`}
    </Typography>
  );
}
